/**
 * When two tracks are the same song.
 *
 * These rules are the desktop's, in `music-catalog/utils/track-occurrence-index.ts`,
 * and are reproduced rather than shared because the two applications have no
 * domain package between them — only `@ceedero/ui`, which owns no product
 * meaning. Reproduced, they can drift, so the tests below assert the specific
 * cases the desktop's own tests assert: a change to one is a change to both.
 *
 * Two copies of a song differing only by "(Remastered 2011)" or a leading track
 * number are one song to a person looking at a duplicate list, so they are one
 * key here.
 */

const VERSION_WORDS = new Set([
  "acoustic",
  "edit",
  "extended",
  "instrumental",
  "live",
  "mix",
  "mono",
  "radio",
  "remaster",
  "remastered",
  "remix",
  "stereo",
  "version",
]);

const VERSION_QUALIFIER_PATTERN =
  /\b(?:acoustic|edit|extended|instrumental|live|mix|mono|radio|remaster(?:ed)?|remix|stereo|version)\b/iu;

const normalizeDiacriticsAndCase = (value: string) =>
  value
    .normalize("NFKD")
    .replaceAll(/[̀-ͯ]/g, "")
    .toLocaleLowerCase();

const normalizeTrackText = (value: string) =>
  normalizeDiacriticsAndCase(value)
    .replaceAll(/[^a-z0-9]+/g, " ")
    .trim();

export const normalizeTrackTitle = (title: string) => {
  // Only a bracketed group that names a version is dropped. "(Live)" is not
  // part of the song's identity; "(Reprise)" is a different piece of music.
  const withoutVersionGroups = title
    .replaceAll(/\([^)]*\)/gu, (group) => (VERSION_QUALIFIER_PATTERN.test(group) ? " " : group))
    .replaceAll(/\[[^\]]*\]/gu, (group) => (VERSION_QUALIFIER_PATTERN.test(group) ? " " : group))
    .replaceAll(/\{[^}]*\}/gu, (group) => (VERSION_QUALIFIER_PATTERN.test(group) ? " " : group));
  const withoutFeaturing = withoutVersionGroups.replace(/\b(?:feat(?:uring)?|ft)\.?\s+.*$/iu, "");
  const normalized = normalizeDiacriticsAndCase(withoutFeaturing)
    .replace(/^\s*\d{1,3}\s*[-_. )]+\s*/, " ")
    .replaceAll(/[()[\]{}]/g, " ")
    .replaceAll(/[^a-z0-9]+/g, " ")
    .trim();

  return normalized
    .split(/\s+/)
    .filter((word) => word && !VERSION_WORDS.has(word) && !/^\d{4}$/.test(word))
    .join(" ");
};

export const normalizeTrackArtist = (artist: string) => normalizeTrackText(artist);

/**
 * Null for a track nobody has named, rather than a shared empty key: two
 * untitled tracks are not the same song.
 */
export const createTrackDuplicateKey = ({ artist, title }: { artist: string; title: string }) => {
  const normalizedTitle = normalizeTrackTitle(title);
  if (!normalizedTitle) {
    return null;
  }
  return JSON.stringify([normalizeTrackArtist(artist), normalizedTitle]);
};
